import { Formik } from "formik";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Creators as ReportActions } from "../../../store/ducks/report";
import * as Yup from "yup";

import { Col, DatePicker, Modal, notification, Row, Select, Spin } from "antd";
import { FormModalReportReadingSheet } from "./styles";

import ButtonStandard from "../../../components/utils/button";

// Services
import { getPicketIndexDropDown } from "../../../services/picketService";
import { findLatest } from "../../../services/picketSupplementSupplyService";
import { processReportTroughReadingSheet } from "../../../services/reportService";
import fileReader from "../../../utils/fileReader";

const validateSchema = Yup.object().shape({
  reportDate: Yup.date().required(),
});

export function ExportReportTroughReadingSheet() {
  const formikRef = useRef();
  const [formRequestReadingSheetReport, setFormRequestReadingSheetReport] =
    useState({
      reportDate: null,
      picketIds: [],
    });

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPicketList, setIsLoadingPicketList] = useState(false);
  const [picketsFiltered, setPicketsFiltered] = useState([]);
  const [lastestTreatDate, setLastestTreatDate] = useState(null);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const { isModalTroughReadingSheetReportVisible: modalVisible } = useSelector(
    (state) => state.report
  );

  const dispatch = useDispatch();

  const getAllPickets = useCallback(async () => {
    setIsLoadingPicketList(true);
    try {
      const {
        data: { results },
      } = await getPicketIndexDropDown({
        groupId,
        farmId,
      });
      const filterPicketsActiveOrPasturage = results.filter(
        (picket) =>
          (picket.status === "Pasturage" || picket.status === "P") &&
          picket.lotAnimalsCount > 0
      );
      setPicketsFiltered(filterPicketsActiveOrPasturage);
    } catch (error) {
    } finally {
      setIsLoadingPicketList(false);
    }
  }, [farmId, groupId]);

  const getLastestPicketSupply = useCallback(async () => {
    try {
      const {
        data: { results },
      } = await findLatest({
        groupId,
        farmId,
      });
      if (results) {
        return results;
      }
      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [farmId, groupId]);

  const closeModal = () => {
    dispatch(ReportActions.hideModal());
  };

  const cleanFields = () => {
    setFormRequestReadingSheetReport({
      reportDate: null,
      picketIds: [],
    });
  };

  const handleSubmit = async (values, actions) => {
    setIsLoading(true);
    notification.info({
      message:
        translation.reportTroughReadingSheetModalRequest.messages
          .preparingReport,
    });
    setTimeout(() => {
      setIsLoading(false);
      cleanFields();
      closeModal();
    }, 100);
    try {
      const { data } = await processReportTroughReadingSheet({
        groupId,
        farmId,
        body: values,
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([data]));
      let link = document.createElement("a");
      link.href = downloadUrl;
      const dayFormat = moment().format("YYYYMMDD");
      const hourFormat = moment().format("HHmm");
      link.setAttribute(
        "download",
        `Ficha_de_Leitura_de_Cocho_${dayFormat}_${hourFormat}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      notification.success({
        message: translation.successGenerateReport,
      });
    } catch (error) {
      if (error?.response) {
        try {
          const { data } = error.response;
          // Read file
          const file = await fileReader(data);

          if (typeof file === "string") {
            // Parse content and retrieve 'message'
            const result = JSON.parse(file);
            if (result.code === 5035) {
              notification.error({
                message:
                  translation.reportTroughReadingSheetModalRequest.messages
                    .noDataFound,
              });
            } else {
              notification.error({
                message: translation.reports.errorGenerateReport,
              });
            }
          }
        } catch (readError) {
          notification.error({
            message: translation.reports.errorGenerateReport,
          });
        }
      } else {
        notification.error({
          message: translation.reports.errorGenerateReport,
        });
      }
    }
  };

  useEffect(() => {
    async function fetchData() {
      getAllPickets();
      const latestTreatDate = await getLastestPicketSupply();
      if (latestTreatDate) {
        setFormRequestReadingSheetReport((prev) => ({
          reportDate: moment(latestTreatDate.supplyDate).add(1, "days"),
          picketIds: [],
        }));
        formikRef.current.resetForm({
          reportDate: moment(latestTreatDate.supplyDate).add(1, "days"),
          picketIds: [],
        });
        setLastestTreatDate(moment(latestTreatDate.supplyDate));
      } else {
        setFormRequestReadingSheetReport((prev) => ({
          reportDate: null,
          picketIds: [],
        }));
        formikRef.current.resetForm({
          reportDate: null,
          picketIds: [],
        });
        setLastestTreatDate(null);
      }
    }
    if (modalVisible === true) {
      fetchData();
    }
  }, [getAllPickets, getLastestPicketSupply, modalVisible]);

  return (
    <Modal
      title={translation.reports.titles.ReportTroughReadingSheet}
      visible={modalVisible}
      centered
      closable
      maskClosable={false}
      footer={null}
      onCancel={closeModal}
    >
      <Formik
        ref={formikRef}
        initialValues={formRequestReadingSheetReport}
        validationSchema={validateSchema}
        onSubmit={handleSubmit}
        render={({
          values,
          errors,
          setFieldValue,
          handleSubmit,
          submitCount,
        }) => (
          <FormModalReportReadingSheet onSubmit={handleSubmit}>
            <Spin spinning={isLoading}>
              <Row type="flex" justify="center">
                <p>
                  {
                    translation.reportTroughReadingSheetModalRequest.messages
                      .informData
                  }
                </p>
              </Row>
              {Object.entries(errors).length > 0 && submitCount > 0 && (
                <Row type="flex" justify="center" align="middle">
                  <label className="error">{translation.error.formError}</label>
                </Row>
              )}
              <Row type="flex" gutter={8}>
                <Col span={24}>
                  <Row>
                    <label
                      className={
                        errors.reportDate && submitCount > 0 ? "error" : ""
                      }
                    >
                      {
                        translation.reportTroughReadingSheetModalRequest.form
                          .date
                      }
                      *
                    </label>
                  </Row>
                  <Row>
                    <DatePicker
                      value={values.reportDate || undefined}
                      format={"DD/MM/YYYY"}
                      disabledDate={(current) =>
                        current.isAfter(moment(), "day")
                      }
                      onChange={(date) => setFieldValue("reportDate", date)}
                    />
                  </Row>
                  {lastestTreatDate && (
                    <Row>
                      <i>{`Último tratamento: ${lastestTreatDate.format(
                        "DD/MM/YYYY"
                      )}`}</i>
                    </Row>
                  )}
                </Col>
              </Row>
              <Row type="flex" gutter={8}>
                <Col span={24}>
                  <Row>
                    <label>
                      {
                        translation.reportTroughReadingSheetModalRequest.form
                          .pickets
                      }
                    </label>
                  </Row>
                  <Row>
                    <Select
                      name="picketIds"
                      value={values.picketIds || undefined}
                      loading={isLoadingPicketList}
                      allowClear
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.props.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      mode="multiple"
                      placeholder={translation.defaultSelectPlaceholder}
                      onChange={(value) => setFieldValue("picketIds", value)}
                    >
                      {picketsFiltered.map((picket) => (
                        <Select.Option key={picket.id} value={picket.id}>
                          {picket.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Row>
                </Col>
              </Row>
              <Row type="flex" style={{ paddingTop: 16 }}>
                <Col span={24}>
                  <Row type="flex" justify="end" align="middle" gutter={24}>
                    <Col>
                      <ButtonStandard
                        type="button"
                        buttonType="type4"
                        size="s"
                        onClick={closeModal}
                      >
                        {translation.buttons.cancel}
                      </ButtonStandard>
                    </Col>
                    <Col>
                      <ButtonStandard type="submit" buttonType="type1" size="s">
                        {translation.buttons.generate}
                      </ButtonStandard>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Spin>
          </FormModalReportReadingSheet>
        )}
      />
    </Modal>
  );
}
